import React, { useState, useEffect, useCallback } from "react";
import { Form, Row, Col, Button, Modal, Badge } from "react-bootstrap";
import axios from "axios";
import config from "../../config";
import { toast } from "react-toastify";
import Select from 'react-select';
import { jwtDecode } from "jwt-decode";
import DataTable from 'react-data-table-component';

const customStyles = {
  rows: {
    style: {
      minHeight: '80px',
    },
  },
  headCells: {
    style: {
      paddingLeft: '8px',
      paddingRight: '8px',
      backgroundColor: '#f8f9fa',
      fontWeight: 'bold',
    },
  },
  cells: {
    style: {
      paddingLeft: '8px',
      paddingTop: '8px',
      paddingBottom: '8px',
      paddingRight: '8px',
    },
  },
  table: {
    style: {
      minWidth: '100%',
    },
  },
};

export default function QualityCheckTab({ purchaseOrderId, items, onUpdate, orderStatus }) {
  const [grns, setGrns] = useState([]);
  const [qualityEntries, setQualityEntries] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const token = localStorage.getItem("token");
  const decoded = jwtDecode(token);
  const createdBy = decoded.userId;

  const [qualityForm, setQualityForm] = useState({
    grnId: "",
    productId: "",
    quality: "",
    isAccepted: true,
    rejectionHandling: null,
    acceptedProductId: null,
    quantity: '',
    remarks: "",
    unit: "",
    createdBy: createdBy
  });

  const [selectedQualityEntry, setSelectedQualityEntry] = useState(null);
  const [changeHandlingForm, setChangeHandlingForm] = useState({
    rejectionHandling: "",
    acceptedProductId: null,
    remarks: ""
  });

  const rejectionOptions = [
    { value: 'HOLD', label: 'Hold for Review' },
    { value: 'RETURN', label: 'Return to Supplier' },
    { value: 'ACCEPT', label: 'Accept with Different Grade' }
  ];

  const fetchData = useCallback(async () => {
    try {
      if (!purchaseOrderId || String(purchaseOrderId).trim().toLowerCase() === 'undefined') {
        console.warn('QualityCheckTab: invalid purchaseOrderId, skipping fetchData', purchaseOrderId);
        return;
      }

      const [grnsRes, qualityRes, productsRes] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/grn/${purchaseOrderId}`),
        axios.get(`${config.API_BASE_URL}/quality-checks/${purchaseOrderId}`),
        axios.get(`${config.API_BASE_URL}/products?category=all&minimal=true&populateGroup=true&populateUnit=true`)
      ]);
      
      setGrns(grnsRes.data);
      setQualityEntries(qualityRes.data);
      setAllProducts(productsRes.data);
    } catch (err) {
      console.error("Fetch data error:", err);
    }
  },[purchaseOrderId]);

  useEffect(() => {
    if (!purchaseOrderId || String(purchaseOrderId).trim().toLowerCase() === 'undefined') {
      console.warn('QualityCheckTab: invalid purchaseOrderId in useEffect, skipping fetchData', purchaseOrderId);
      return;
    }

    fetchData();
  }, [purchaseOrderId, fetchData]);

  const handleQualitySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${config.API_BASE_URL}/quality-checks`, {
        ...qualityForm,
        purchaseOrderId
      });
      toast.success("Quality check entry added");
      setQualityForm({
        grnId: "",
        productId: "",
        quality: "",
        isAccepted: true,
        rejectionHandling: null,
        acceptedProductId: null,
        quantity: '',
        remarks: "",
        unit: "",
        createdBy: createdBy
      });
      setShowAddModal(false);
      fetchData();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error("Failed to add quality check entry");
    } finally {
      setIsSubmitting(false);
    }
  };

  // const handleChangeHandlingSubmit = async (e) => {
  //   e.preventDefault();
  //   setIsSubmitting(true);
  //   try {
  //     await axios.put(`${config.API_BASE_URL}/quality-checks/${selectedQualityEntry._id}`, {
  //       ...changeHandlingForm
  //     });
  //     toast.success("Change handling updated");
  //     setChangeHandlingForm({
  //       rejectionHandling: "",
  //       acceptedProductId: null,
  //       remarks: ""
  //     });
  //     setShowEditModal(false);
  //     fetchData();
  //   } catch (err) {
  //     toast.error("Failed to update change handling");
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

  const handleChangeHandling = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.patch(`${config.API_BASE_URL}/quality-checks/${selectedQualityEntry._id}`, {
        rejectionHandling: changeHandlingForm.rejectionHandling,
        acceptedProductId: changeHandlingForm.rejectionHandling === 'ACCEPT' ? 
          changeHandlingForm.acceptedProductId : null,
        remarks: `${selectedQualityEntry.remarks}\nChanged handling to: ${changeHandlingForm.rejectionHandling}${changeHandlingForm.remarks ? ' - ' + changeHandlingForm.remarks : ''}`
      });
      
      toast.success("Quality check handling updated");
      setShowEditModal(false);
      setSelectedQualityEntry(null);
      setChangeHandlingForm({
        rejectionHandling: "",
        acceptedProductId: null,
        remarks: ""
      });
      fetchData(); // Refresh data
    } catch (err) {
      toast.error("Failed to update quality check handling");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getGRNBadge = (grnId) => {
    const grn = grns.find(g => g._id === grnId || g._id.toString() === grnId);
    return grn ? grn.grnId : 'Unknown GRN';
  };

  const getAvailableProducts = () => {
    const poProductIds = items.map(item => item.productId?._id).filter(Boolean);
    const poProducts = allProducts.filter(product => poProductIds.includes(product._id));
    const uniqueGroupIds = [...new Set(poProducts.map(product => product.groupId?._id || product.groupId).filter(Boolean))];
    return allProducts.filter(product => {
      const productGroupId = product.groupId?._id || product.groupId;
      return uniqueGroupIds.includes(productGroupId);
    });
  };
  // const getAvailablePOProducts = () => {
  //   const poProductIds = items.map(item => item.productId?._id).filter(Boolean);
  //   const poProducts = allProducts.filter(product => poProductIds.includes(product._id));
  //   const uniqueGroupIds = [...new Set(poProducts.map(product => product.groupId?._id || product.groupId).filter(Boolean))];
  //   return poProducts;
  //   // return allProducts.filter(product => {
  //   //   const productGroupId = product.groupId?._id || product.groupId;
  //   //   return uniqueGroupIds.includes(productGroupId);
  //   // });
  // };

  const columns = [
    {
      name: 'GRN',
      selector: row => getGRNBadge(row.grnId?._id),
      cell: row => <Badge bg="info">{getGRNBadge(row.grnId?._id)}</Badge>,
      sortable: true,
      width: '125px',
    },
    {
      name: 'Product',
      selector: row => row.productId?.productName,
      sortable: true,
      wrap: true,
      // width: '160px',
    },
    {
      name: 'Quality',
      selector: row => row.quality,
      sortable: true,
      wrap: true,
      // width: '150px',
    },
    {
      name: 'Quantity',
      selector: row => `${row.quantity} ${row.unit}`,
      sortable: true,
      right: true,
      // width: '120px',
    },
    {
      name: 'Status',
      cell: row => (
        <div className="w-100">
          {row.isAccepted ? (
            <Badge bg="success" className="w-100 text-center">Accepted</Badge>
          ) : (
            <Badge bg="danger" className="w-100 text-center">Rejected</Badge>
          )}
        </div>
      ),
      sortable: true,
      width: '100px',
      center: true,
    },
    {
      name: 'Rejection Details',
      cell: row => {
        if (!row.isAccepted) {
          return (
            <div className="d-flex flex-column gap-1">
              <div className="d-flex align-items-center">
                <Badge bg="warning" className="me-1">
                  {row.rejectionHandling === 'HOLD' && (
                    <>
                      On Hold
                      <Button 
                        size="sm" 
                        variant="link" 
                        className="ms-2 p-0 text-decoration-none"
                        onClick={() => {
                          setSelectedQualityEntry(row);
                          setShowEditModal(true);
                        }}
                      >
                        <i className="fas fa-edit text-primary"></i>
                      </Button>
                    </>
                  )}
                  {row.rejectionHandling === 'RETURN' && 'Return to Supplier'}
                  {row.rejectionHandling === 'ACCEPT' && 'Accept as Following Product'}
                </Badge>
              </div>
              {row.rejectionHandling === 'ACCEPT' && row.acceptedProductId && (
                <small className="text-muted fw-bold">
                  As: {allProducts.find(p => p._id === row.acceptedProductId._id)?.productName || 'Unknown Product'}
                </small>
              )}
            </div>
          );
        }
        return null;
      },
      width: '175px',
      wrap: true,
    },
    {
      name: 'Remarks',
      selector: row => row.remarks,
      wrap: true,
      grow: 2,
      minWidth: '200px',
    },
    {
      name: 'Created By',
      selector: row => row.createdBy?.name,
      sortable: true,
      width: '150px',
      wrap: true,
    },
    {
      name: 'Date',
      selector: row => new Date(row.createdAt).toLocaleDateString('en-IN'),
      sortable: true,
      right: true,
      width: '120px',
    },
  ];

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Quality Check Entries</h5>
      </div>

      <DataTable
        columns={columns}
        data={qualityEntries}
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        customStyles={customStyles}
        striped
        highlightOnHover
        responsive
        subHeader
        dense
        fixedHeader
        fixedHeaderScrollHeight="calc(100vh - 300px)"
        subHeaderComponent={
          <div className="d-flex justify-content-between align-items-center w-100 mb-3">
            <div className="d-flex gap-2 align-items-center">
              <input
                type="text"
                placeholder="Search..."
                className="form-control form-control-sm"
                style={{ width: '200px' }}
                onChange={e => {
                  // Add search functionality here
                }}
              />
            </div>
            <Button
              variant="success"
              onClick={() => setShowAddModal(true)}
              // disabled={orderStatus === 'Fully Received' || orderStatus === 'Closed' || orderStatus === 'Cancelled'}
            >
              <i className="fas fa-plus me-2"></i>Add Quality Check
            </Button>
          </div>
        }
      />

      {/* Add Quality Check Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Quality Check Entry</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleQualitySubmit}>
          <Modal.Body>
            <Row className="gy-3">
              <Col md={6}>
                <Form.Label>GRN *</Form.Label>
                <Form.Select
                  value={qualityForm.grnId}
                  onChange={(e) => setQualityForm(prev => ({...prev, grnId: e.target.value}))}
                  required
                >
                  <option value="">Select GRN</option>
                  {grns.map(grn => (
                    <option key={grn._id} value={grn._id}>{grn.grnId}</option>
                  ))}
                </Form.Select>
              </Col>
    
              <Col md={6}>
                <Form.Label>Product *</Form.Label>
                <Form.Select
                  value={qualityForm.productId}
                  onChange={(e) => {
                    const product = getAvailableProducts().find(p => p._id === e.target.value);
                    setQualityForm(prev => ({
                      ...prev,
                      productId: e.target.value,
                      unit: product?.unitId?.unitCode || ""
                    }));
                  }}
                  required
                >
                  <option value="">Select Product</option>
                  {items.map(product => (
                    <option key={product.productId?._id} value={product.productId?._id}>
                      {product.productId?.productName} ({product.orderedQuantity}{product.unit})
                    </option>
                  ))}
                  {/* {getAvailablePOProducts().map(product => (
                    <option key={product._id} value={product._id}>
                      {product.productName}
                    </option>
                  ))} */}
                </Form.Select>
              </Col>

              <Col md={12}>
                <Form.Label>Quality Specification</Form.Label>
                <Form.Control
                  value={qualityForm.quality}
                  onChange={(e) => setQualityForm(prev => ({...prev, quality: e.target.value}))}
                  placeholder="Enter quality details"
                />
              </Col>

              <Col md={6}>
                <Form.Label>Quantity *</Form.Label>
                <Form.Control
                  type="number"
                  value={qualityForm.quantity}
                  onChange={(e) => setQualityForm(prev => ({...prev, quantity: Number(e.target.value)}))}
                  required
                />
              </Col>

              <Col md={6}>
                <Form.Label>Quality Status</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    label="Accepted"
                    name="qualityStatus"
                    checked={qualityForm.isAccepted}
                    onChange={() => setQualityForm(prev => ({...prev, isAccepted: true, rejectionHandling: null, acceptedProductId: null}))}
                  />
                  <Form.Check
                    type="radio"
                    label="Rejected"
                    name="qualityStatus"
                    checked={!qualityForm.isAccepted}
                    onChange={() => setQualityForm(prev => ({...prev, isAccepted: false}))}
                  />
                </div>
              </Col>

              {!qualityForm.isAccepted && (
                <Col md={12}>
                  <Form.Label>Rejection Handling</Form.Label>
                  <Select
                    value={rejectionOptions.find(opt => opt.value === qualityForm.rejectionHandling)}
                    options={rejectionOptions}
                    onChange={(selected) => setQualityForm(prev => ({
                      ...prev,
                      rejectionHandling: selected?.value || null,
                      acceptedProductId: null
                    }))}
                    isClearable
                  />
                </Col>
              )}

              {!qualityForm.isAccepted && qualityForm.rejectionHandling === 'ACCEPT' && (
                <Col md={12}>
                  <Form.Label>Accept As Product</Form.Label>
                  <Form.Select
                    value={qualityForm.acceptedProductId}
                    onChange={(e) => setQualityForm(prev => ({...prev, acceptedProductId: e.target.value}))}
                  >
                    <option value="">Select Product</option>
                    {getAvailableProducts().map(product => (
                      <option key={product._id} value={product._id}>
                        {product.productName}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              )}

              <Col md={12}>
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={qualityForm.remarks}
                  onChange={(e) => setQualityForm(prev => ({...prev, remarks: e.target.value}))}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Adding Entry...' : 'Add Entry'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Quality Check Modal */}
      <Modal show={showEditModal} onHide={() => {
        setShowEditModal(false);
        setSelectedQualityEntry(null);
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Change Rejection Handling</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleChangeHandling}>
          <Modal.Body>
            <Row className="gy-3">
              <Col md={12}>
                <Form.Label>Product</Form.Label>
                <p className="form-control-plaintext">
                  {selectedQualityEntry?.productId?.productName}
                </p>
              </Col>

              <Col md={12}>
                <Form.Label>Change Handling To</Form.Label>
                <Form.Select
                  value={changeHandlingForm.rejectionHandling}
                  onChange={(e) => setChangeHandlingForm(prev => ({
                    ...prev,
                    rejectionHandling: e.target.value,
                    acceptedProductId: null
                  }))}
                  required
                >
                  <option value="">Select Handling</option>
                  <option value="RETURN">Return to Supplier</option>
                  <option value="ACCEPT">Accept as Different Product</option>
                </Form.Select>
              </Col>

              {changeHandlingForm.rejectionHandling === 'ACCEPT' && (
                <Col md={12}>
                  <Form.Label>Accept As Product</Form.Label>
                  <Form.Select
                    value={changeHandlingForm.acceptedProductId}
                    onChange={(e) => setChangeHandlingForm(prev => ({
                      ...prev,
                      acceptedProductId: e.target.value
                    }))}
                    required
                  >
                    <option value="">Select Product</option>
                    {getAvailableProducts().map(product => (
                      <option key={product._id} value={product._id}>
                        {product.productName}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              )}

              <Col md={12}>
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={changeHandlingForm.remarks}
                  onChange={(e) => setChangeHandlingForm(prev => ({
                    ...prev,
                    remarks: e.target.value
                  }))}
                  placeholder="Add any additional remarks about this change"
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" disabled={isSubmitting} onClick={() => {
              setShowEditModal(false);
              setSelectedQualityEntry(null);
            }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
